/** Plain-English descriptions of the common 5-field cron patterns; anything else returns null (show the raw text). */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT = DAYS.map((d) => d.slice(0, 3));

const isInt = (s: string) => /^\d+$/.test(s);
const pad = (n: number) => String(n).padStart(2, '0');

function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** `1-5` → Monday–Friday; `0,6` → Sat/Sun; `1,3,5` → Mon, Wed, Fri. Null when not a plain day list. */
function describeDays(field: string): string | null {
  if (field === '1-5') return 'weekdays';
  if (field === '0,6' || field === '6,0' || field === '6,7' || field === '6-7') return 'weekends';
  const parts = field.split(',');
  const days: number[] = [];
  for (const p of parts) {
    const r = /^(\d)-(\d)$/.exec(p);
    if (r) {
      const [a, b] = [Number(r[1]), Number(r[2])];
      if (a > b || b > 7) return null;
      for (let i = a; i <= b; i++) days.push(i % 7);
    } else if (/^\d$/.test(p) && Number(p) <= 7) days.push(Number(p) % 7);
    else return null;
  }
  const uniq = [...new Set(days)];
  if (uniq.length === 1) return DAYS[uniq[0]];
  return uniq.map((d) => SHORT[d]).join(', ');
}

/** `0 2 * * *` → `Daily at 02:00`; `null` when the pattern is not one we describe. */
export function cronToText(cron: string): string | null {
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5) return null;
  const [min, hour, dom, mon, dow] = f;
  if (mon !== '*') return null;

  // every N minutes / every minute
  if (hour === '*' && dom === '*' && dow === '*') {
    if (min === '*') return 'Every minute';
    const step = /^\*\/(\d+)$/.exec(min);
    if (step) return Number(step[1]) === 1 ? 'Every minute' : `Every ${Number(step[1])} minutes`;
    if (isInt(min) && Number(min) < 60)
      return Number(min) === 0 ? 'Every hour' : `Every hour at ${pad(Number(min))} past`;
    return null;
  }
  if (!isInt(min) || Number(min) > 59) return null;
  const m = Number(min);

  // every N hours
  const hStep = /^\*\/(\d+)$/.exec(hour);
  if (hStep && dom === '*' && dow === '*') {
    const n = Number(hStep[1]);
    const at = m ? ` at ${pad(m)} past` : '';
    return n === 1 ? `Every hour${at}` : `Every ${n} hours${at}`;
  }

  // specific times: `H` or `H1,H2`
  const hours = hour.split(',');
  if (!hours.every((h) => isInt(h) && Number(h) < 24)) return null;
  const at = hours.map((h) => `${pad(Number(h))}:${pad(m)}`).join(', ');

  if (dom === '*' && dow === '*') return `Daily at ${at}`;
  if (dom === '*') {
    const days = describeDays(dow);
    if (!days) return null;
    if (days === 'weekdays') return `Weekdays at ${at}`;
    if (days === 'weekends') return `Weekends at ${at}`;
    return `Every ${days} at ${at}`;
  }
  if (dow === '*') {
    if (dom === 'L') return `Monthly on the last day at ${at}`;
    if (isInt(dom) && Number(dom) >= 1 && Number(dom) <= 31)
      return `Monthly on the ${ordinal(Number(dom))} at ${at}`;
  }
  return null;
}

/** Presets offered by the schedule editor. */
export const CRON_PRESETS: { label: string; cron: string }[] = [
  { label: 'Daily 02:00', cron: '0 2 * * *' },
  { label: 'Weekdays 02:00', cron: '0 2 * * 1-5' },
  { label: 'Weekly Sun 03:00', cron: '0 3 * * 0' },
  { label: 'Monthly 1st 03:00', cron: '0 3 1 * *' },
];

/** Five whitespace-separated fields of cron characters; the server (Cronos) is the real validator. */
export const looksLikeCron = (s: string) => /^([\d*/,\-LW#?]+\s+){4}[\d*/,\-LW#?A-Za-z]+$/.test(s.trim());
